import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["todo", "in_progress", "done", "blocked"];
const empty = { title: "", description: "", status: "todo", priority: "normal", due_date: "" };

export default function Tasks() {
  const { t } = useLang();
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks");
      setTasks(data);
    } catch (error) {
      console.error("Failed to load tasks:", error);
      toast.error("Failed to load tasks");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => payload[k] === "" && delete payload[k]);
      await api.post("/tasks", payload); toast.success(t("common.create")); setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    const task = tasks.find(x => x.task_id === id);
    try { await api.patch(`/tasks/${id}`, { title: task.title, status }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/tasks/${id}`); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <DashboardLayout title={t("tasks.title")} subtitle={t("tasks.subtitle")}
      right={<button data-testid="task-new" onClick={() => setOpen(true)} className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white font-medium text-sm flex items-center gap-2 hover:bg-blue-400"><Plus className="w-4 h-4" /> {t("tasks.new")}</button>}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="tasks-board">
        {STATUSES.map((s) => (
          <div key={s} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-300">{t(`tasks.statuses.${s}`)}</div>
              <div className="text-xs text-zinc-500">{tasks.filter(x => x.status === s).length}</div>
            </div>
            <div className="space-y-2 min-h-[100px]">
              {tasks.filter(x => x.status === s).map((tk) => (
                <div key={tk.task_id} className="rounded-md border border-zinc-800 bg-zinc-950 p-3 text-sm" data-testid={`task-card-${tk.task_id}`}>
                  <div className="text-zinc-100">{tk.title}</div>
                  {tk.due_date && <div className="text-xs text-zinc-500 mt-0.5">Due {tk.due_date}</div>}
                  <div className="mt-2 flex items-center gap-1">
                    <select value={tk.status} onChange={(e) => setStatus(tk.task_id, e.target.value)} className="flex-1 h-7 bg-zinc-900 border border-zinc-800 rounded text-[11px] px-1" data-testid={`task-status-${tk.task_id}`}>
                      {STATUSES.map(ss => <option key={ss} value={ss}>{ss}</option>)}
                    </select>
                    <button onClick={() => remove(tk.task_id)} data-testid={`task-del-${tk.task_id}`} className="h-7 w-7 grid place-items-center rounded text-zinc-400 hover:text-rose-400 hover:bg-zinc-800"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="task-dialog">
          <DialogHeader><DialogTitle>{t("tasks.new")}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <input data-testid="task-field-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            <textarea data-testid="task-field-desc" rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm" />
            <input data-testid="task-field-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
            <button data-testid="task-save" onClick={create} className="btn-scale h-9 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400">{t("common.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
